import {
  fetchYardInspections,
  type FetchYardInspectionsParams,
  type YardInspection,
} from "../../../store/oasis/yardInspectionsStore";

// ─── Traer el rango completo, no la primera página ───────────────────────────
//
// El historial pedía `limit: 200` sin `page`, y el backend deja `page = 1`. Por
// encima de 200 inspecciones en el rango la pantalla describía sólo las 200 más
// recientes, sin ningún aviso. Lo que más pega no es la lista — es que los
// conteos de las píldoras de estado salen del mismo conjunto: "Completas 140"
// significaba 140 ENTRE LAS 200 MÁS RECIENTES y se leía como un total.
//
// Los conteos no se pueden delegar a una consulta agregada del backend: se
// derivan de `deriveInspectionStatus(i, now)` en el cliente (`missing_exit`
// depende del reloj) y se cuentan sobre `baseFiltered`, que ya aplicó patio,
// vehículo, grupo y búsqueda. Un total del servidor y una lista del cliente se
// contradirían en cuanto hubiera un filtro puesto. La forma de que los conteos
// sean ciertos es que el cliente tenga el conjunto entero.
//
// Traerlo entero es barato del lado del servidor: `/yardInspectionList` ya lee
// TODOS los documentos de la empresa en memoria para poder filtrar por
// (createdAt OR entry_timestamp OR exit_timestamp) — Firestore no sabe expresar
// ese OR — y recién después pagina. Pedir 500 o 200 cuesta lo mismo en lecturas;
// lo único que cambia es lo que viaja.

/** Tope de `limit` que acepta el backend (hikvisionAccessController.js). */
export const YARD_INSPECTIONS_PAGE_SIZE = 500;

// Techo de seguridad. Con 500 por página cubre 10 000 inspecciones en el rango;
// hoy hay 81 en producción y el sync crea una por entrada. Si alguna vez se
// toca, la pantalla lo DICE (`truncated`) en vez de mentir un total.
export const YARD_INSPECTIONS_MAX_PAGES = 20;

export interface AllYardInspections {
  data: YardInspection[];
  /** Total que informa el backend para el rango, sin truncar. */
  total: number;
  /** `true` si se alcanzó el techo y `data` no es todo el rango. */
  truncated: boolean;
}

export async function fetchAllYardInspections(
  params: Omit<FetchYardInspectionsParams, "limit" | "page"> = {},
): Promise<AllYardInspections> {
  const first = await fetchYardInspections({
    ...params,
    limit: YARD_INSPECTIONS_PAGE_SIZE,
    page: 1,
  });

  const totalPages = first.pagination?.totalPages ?? 1;
  const total = first.pagination?.total ?? first.data.length;
  const lastPage = Math.min(totalPages, YARD_INSPECTIONS_MAX_PAGES);

  // El caso normal (un solo request) sale por acá: mismo costo que antes.
  if (lastPage <= 1) {
    return { data: first.data, total, truncated: totalPages > lastPage };
  }

  const rest = await Promise.all(
    Array.from({ length: lastPage - 1 }, (_, i) =>
      fetchYardInspections({
        ...params,
        limit: YARD_INSPECTIONS_PAGE_SIZE,
        page: i + 2,
      }),
    ),
  );

  // Dedup por id. Las páginas se piden en paralelo contra un snapshot que el
  // servidor rearma en cada request, y el sync puede insertar una inspección
  // entre medio: como el orden es createdAt desc, una inserción corre la
  // ventana y el mismo documento puede caer en dos páginas. Sin dedup se
  // duplicaría una fila y, peor, un conteo.
  const seen = new Set<string>();
  const data: YardInspection[] = [];
  for (const page of [first, ...rest]) {
    for (const inspection of page.data) {
      if (seen.has(inspection.id)) continue;
      seen.add(inspection.id);
      data.push(inspection);
    }
  }

  return { data, total, truncated: totalPages > lastPage };
}
